import { useAuth } from '@/context/Auth';
import { useContact } from '@/context/Contact';
import { useOidc } from '@/context/Oidc';
import { usePageModels } from '@/context/PageModels';
import { DataLayerEvents } from '@/datalayer/App.datalayer.constants';
import { cleanObject } from '@/utils/objects';
import React from 'react';
import { useDataLayerInterceptor } from './useDataLayerInterceptor';

export interface PageLoadMetadata {
  login_status: DatalayerLoginStatuses;
  user_id?: string;
  account_type?: string;
  registration_status?: string;
  gen_beta_participant_status?: string;
  portal_tracking_status?: string;
  content_group: string;
  content_group2: string;
  content_group3: string;
  content_group4: string;
}

enum DatalayerLoginStatuses {
  LOGGED_IN = 'logged_in',
  ANONYMOUS = 'anonymous',
}

const PAGE_LOAD_STORAGE_KEY = 'datalayer.page_load';

export const getDataLayerPageLoadMetadata = (): Partial<PageLoadMetadata> => {
  try {
    const storedInformation = sessionStorage.getItem(PAGE_LOAD_STORAGE_KEY);
    return JSON.parse(storedInformation ?? '{}');
  } catch (e) {
    return {};
  }
};

const DATALAYER_PATH_KEY = 'datalayer.original_path';

export const getDatalayerPageViewMetadata = () => ({
  original_page_path: JSON.parse(window.sessionStorage.getItem(DATALAYER_PATH_KEY) ?? '{}')
    .originalPagePath,
  page_path: window.document.location.href?.replace(window.location.origin, ''),
});

export const useDataLayerPageLoad = () => {
  const pageModels = usePageModels();
  const { isLoading: isOidcLoading } = useOidc();
  const { isAuthorized, isAuthorizing } = useAuth();
  const { contactInfo, isLoading: isContactLoading } = useContact();
  const [isPageLoaded, setIsPageLoaded] = React.useState(false);
  const emittedRef = React.useRef(false);
  const isLoggedIn = React.useMemo(() => !!Object.keys(contactInfo ?? {}).length, [contactInfo]);
  const { isReady: isDataLayerInterceptorReady } = useDataLayerInterceptor();

  /**
   * This function will emit the react-page-loaded event
   */
  const emitReactPageLoaded = React.useCallback(() => {
    const successEvent = new CustomEvent(DataLayerEvents.PAGE_LOAD);
    window.dispatchEvent(successEvent);
  }, []);

  /**
   * This effect will emit the react-page-loaded event
   * after the user is logged in or not.
   */
  React.useEffect(() => {
    if (!isDataLayerInterceptorReady) return;
    if (isOidcLoading || emittedRef.current) return;
    if (isAuthorizing) return;
    if (!!isAuthorized && isContactLoading) return;

    // Storing the metadata to be used in page load in the index page

    // Using cleanObject to remove null and undefined values
    const metadata: PageLoadMetadata = cleanObject(
      {
        login_status: isLoggedIn
          ? DatalayerLoginStatuses.LOGGED_IN
          : DatalayerLoginStatuses.ANONYMOUS,
        user_id: contactInfo?.contactId,
        account_type: contactInfo?.accountType,
        registration_status: contactInfo?.portalRegistrationStatus,
        gen_beta_participant_status: contactInfo?.betaParticipantStatus,
        portal_tracking_status: contactInfo?.portalPrivacyConsent,
        content_group: pageModels?.contentGroup,
        content_group2: pageModels?.contentGroup2,
        content_group3: pageModels?.contentGroup3,
        content_group4: pageModels?.contentGroup4,
        web_role: contactInfo?.webRoleNames?.join('|'),
        adx_identity_lastsuccessfullogin: contactInfo?.adx_identity_lastsuccessfullogin,
        gen_territorynumber: contactInfo?.gen_territorynumber,
      },
      ['content_group', 'content_group2', 'content_group3', 'content_group4'], // adding key exception, these keys will be baypassed by the cleaning
    ) as PageLoadMetadata;

    sessionStorage.setItem(PAGE_LOAD_STORAGE_KEY, JSON.stringify(metadata));

    emitReactPageLoaded();
    setIsPageLoaded(true);
    emittedRef.current = true;
  }, [
    isDataLayerInterceptorReady,
    contactInfo?.accountType,
    contactInfo?.contactId,
    contactInfo?.portalRegistrationStatus,
    emitReactPageLoaded,
    isAuthorized,
    isAuthorizing,
    isContactLoading,
    isLoggedIn,
    isOidcLoading,
    contactInfo,
    pageModels,
  ]);

  return { isLoaded: isPageLoaded };
};
